import React from 'react';

import dynamic from 'next/dynamic';

import { Awaited, EntryComponent, IRenderContext, LoaderFn } from './entries';

// Keep this helper client-only. Next's dynamic import preloading depends on
// seeing the literal import() at the dynamic() callsite; hiding it behind this
// generic CMS helper can SSR HTML without preloading the matching client chunk.
export const dynamicEntry = <P, L>(
  importFn: () => Promise<{ default: EntryComponent<P, L> }>,
): EntryComponent<P, L> => {
  const DynamicComponent = dynamic(
    () =>
      importFn().then((mod) => {
        // eslint-disable-next-line react/display-name
        const Component = (props: {
          inputProps: P;
          loaderProps?: Awaited<L>;
          context?: IRenderContext;
        }) => {
          const { inputProps, loaderProps, context } = props;
          return React.createElement(
            React.Fragment,
            null,
            mod.default.render(inputProps, loaderProps, context),
          );
        };
        return Component;
      }),
    { ssr: false },
  );

  const loaderWrapper: LoaderFn<P, L> = async (inputProps, root, pageProps) => {
    const mod = await importFn();
    if (mod.default.loader) {
      return mod.default.loader(inputProps, root, pageProps);
    }
    return null as unknown as L;
  };

  const Component: EntryComponent<P, L> = {
    render: (inputProps, loaderProps, context) =>
      React.createElement(DynamicComponent, {
        inputProps,
        loaderProps,
        context,
      }),
    loader: loaderWrapper,
  };

  return Component;
};
